/* Prescription Builder — Inner Bloom Dispensary */
'use strict';

/* ── CONSTANTS ── */
const DROPPER_PRICE = 1.50;
const ESS_MAX = 6;
const HERB_LOW_STOCK = 200;
const COMP_LOW_STOCK = 100;
const HERB_ALLOWED_VENDORS = ['MediHerb','OptimalRx','Herbal Extract Co','PPC'];

/* ── DATA ── */
let HERBS = [], ESSENCES = [], COMPOUNDS = [], CREAM_BASES = [];
let dataLoaded = false;

/* ── STATE ── */
let activeTab = 'herbal';
let cartCount = 0, toastTmr = null;
let chipExpanded = { herbal: false, compound: false, cream: false };
let selPatient = null, acFocusIdx = -1;
let HS = { bottle: null, bottleMax: 0, material: 'glass', dropper: false, qtys: {}, essenceQtys: {}, loaded: null };
let CS = { duration: 0, doses: {}, loaded: null };
let KS = { base: null, qtys: {}, essenceQtys: {}, loaded: null };
let drawerItem = null, drawerTab = null;

/* ── MOCK PATIENTS (Phase 2: replace with Railway API) ── */
const D30 = new Date(); D30.setDate(D30.getDate() - 30);
const PATIENTS = [
  {id:'p1', name:'Sarah Mitchell', formulas:[
    {id:'hf1', type:'herbal', name:'Sleep & Anxiety Support', date:'2026-03-15', summary:'200mL · 5 herbs',
     data:{bottle:200, material:'glass', dropper:true, dosage:'7.5mL 2x/day before meals', qtys:{}, essenceQtys:{}}},
    {id:'cf1', type:'compound', name:'Gut Repair Protocol', date:'2026-02-10', summary:'28 days · 3 ingredients',
     data:{duration:28, dosage:'1 scoop daily in water before breakfast', doses:{}}},
  ]},
  {id:'p2', name:'James Thornton', formulas:[
    {id:'hf3', type:'herbal', name:'Stress Blend', date:'2026-04-20', summary:'500mL · 6 herbs',
     data:{bottle:500, material:'glass', dropper:false, dosage:'10mL 2x/day', qtys:{}, essenceQtys:{}}},
  ]},
  {id:'p3', name:'Emma Clarke', formulas:[
    {id:'cf2', type:'compound', name:'Methylation Support', date:'2026-04-15', summary:'60 days · 3 ingredients',
     data:{duration:60, dosage:'Nightly before bed in warm water', doses:{}}},
    {id:'cr1', type:'cream', name:'Eczema Cream', date:'2026-05-10', summary:'2 herbs',
     data:{base:null, dosage:'Apply to affected areas 2x/day', qtys:{}, essenceQtys:{}}},
  ]},
  {id:'p4', name:'Robert Kim', formulas:[]},
  {id:'p5', name:'Priya Patel', formulas:[
    {id:'hf4', type:'herbal', name:'Liver & Detox Support', date:'2026-05-05', summary:'200mL · 4 herbs',
     data:{bottle:200, material:'glass', dropper:false, dosage:'7.5mL 2x/day after meals', qtys:{}, essenceQtys:{}}},
  ]},
];

/* ── DATA LOADING ── */
async function fetchCollectionPage(handle, page, view) {
  const r = await fetch(`/collections/${handle}?view=${view}&page=${page}`);
  if (!r.ok) throw new Error(`Failed to fetch ${handle}`);
  return r.json();
}

async function fetchAllPages(handle, view) {
  const first = await fetchCollectionPage(handle, 1, view);
  let products = first.products.filter(p => p.id);
  for (let p = 2; p <= first.pages; p++) {
    const next = await fetchCollectionPage(handle, p, view);
    products = products.concat(next.products.filter(q => q.id));
  }
  return products;
}

function normalizeHerb(p) {
  return {
    id: p.id, variantId: p.variant_id, name: p.title, vendor: p.vendor,
    dosage: p.type || p.weekly_dosage || '',
    price: typeof p.price === 'number' ? p.price / 100 : null,
    stock: p.inventory, image: p.image || null, created: p.created_at,
    description: p.description || '', available: p.available
  };
}

function normalizeEssence(p) {
  return {
    id: p.id, variantId: p.variant_id, name: p.title, vendor: p.vendor,
    price: typeof p.price === 'number' ? p.price / 100 : null,
    image: p.image || null, image2: p.image2 || null,
    description: p.description || '', available: p.available
  };
}

function normalizeCompound(p) {
  return {
    id: p.id, variantId: p.variant_id, name: p.title, vendor: p.vendor,
    price: typeof p.price === 'number' ? p.price / 100 : null,
    stock: p.inventory, image: p.image || null, created: p.created_at,
    description: p.description || '', recDose: p.recommended_dose || '',
    elemental: p.elemental_dose || '', warnings: p.warnings || '',
    isMulti: !!(p.elemental_dose), available: p.available
  };
}

function normalizeCreamBase(p) {
  return {
    id: p.id, variantId: p.variant_id, name: p.title,
    price: typeof p.price === 'number' ? p.price / 100 : null,
    image: p.image || null, description: p.description || '', available: p.available
  };
}

async function initBuilder() {
  const cfg = window.PB_CONFIG;
  try {
    const herbPromises = cfg.herbCollections.map(h => fetchAllPages(h, 'formula-data'));
    const [herbResults, compResults, creamResults, essenceResults] = await Promise.all([
      Promise.all(herbPromises),
      fetchAllPages(cfg.compoundingCollection, 'compound-data'),
      fetchAllPages(cfg.creamsCollection, 'formula-data'),
      fetchAllPages(cfg.essencesCollection, 'formula-data')
    ]);

    HERBS = herbResults.flat()
      .filter(p => p.available && HERB_ALLOWED_VENDORS.includes(p.vendor))
      .map(normalizeHerb)
      .sort((a, b) => a.name.localeCompare(b.name));

    COMPOUNDS = compResults
      .filter(p => p.available)
      .map(normalizeCompound)
      .sort((a, b) => a.name.localeCompare(b.name));

    CREAM_BASES = creamResults
      .filter(p => p.available)
      .map(normalizeCreamBase)
      .sort((a, b) => a.name.localeCompare(b.name));

    ESSENCES = essenceResults
      .filter(p => p.available)
      .map(normalizeEssence);

    dataLoaded = true;
    renderHerbTable();
    renderCompTable();
    renderCreamBases();
    renderCreamTable();
    updatePanel();
    updateFloatBar();
  } catch (err) {
    console.error('PB init error:', err);
    ['herb-tblScroll', 'comp-tbody', 'cream-tbody'].forEach(id => {
      const el = document.getElementById(id);
      if (el) el.innerHTML = '<p style="padding:16px;color:var(--warn);font-size:13px">Unable to load catalogue. Please refresh the page.</p>';
    });
  }
}

/* ── HELPERS ── */
function isNew(d) { return d && new Date(d) > D30; }
function esc(s) { return String(s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;'); }
function rnd(n, d = 2) { return Math.round(n * Math.pow(10,d)) / Math.pow(10,d); }
function isWhole(n) { return Math.abs(n - Math.round(n)) < 0.0001; }
function formatDate(d) { return new Date(d).toLocaleDateString('en-AU',{day:'numeric',month:'short',year:'numeric'}); }
function showToast(msg) { const t = document.getElementById('toast'); t.textContent = msg; t.classList.add('show'); clearTimeout(toastTmr); toastTmr = setTimeout(() => t.classList.remove('show'), 3600); }
function openModal(id) { document.getElementById(id).classList.add('open'); }
function closeModal(id) { document.getElementById(id).classList.remove('open'); }
function clearSrch(inpId, btnId, renderFn) { const i = document.getElementById(inpId); if (i) i.value = ''; const b = document.getElementById(btnId); if (b) b.style.display = 'none'; renderFn(); }

function stockCell(stock, threshold = HERB_LOW_STOCK) {
  if (stock > threshold) return '<span class="in-stock">In stock</span>';
  if (stock > 0) return `<span class="ls-badge">LOW STOCK:<br>${stock} remaining</span>`;
  return '<span class="ls-badge">Out of stock</span>';
}
function newBadge(item) { return isNew(item.created) ? '<span class="badge-new">New</span>' : ''; }

/* ── PATIENT AUTOCOMPLETE ── */
function onPatientInput() {
  const val = document.getElementById('patientInp').value.trim();
  document.getElementById('patientClear').style.display = val ? 'block' : 'none';
  if (!val) { document.getElementById('patientDrop').classList.remove('open'); selPatient = null; updatePatientHistoryPanel(); return; }
  const matches = PATIENTS.filter(p => p.name.toLowerCase().includes(val.toLowerCase())).slice(0, 6);
  renderAcDrop(matches, val);
  document.getElementById('patientNewBadge').style.display = 'none';
}
function onPatientFocus() { if (document.getElementById('patientInp').value.trim()) onPatientInput(); }
function onPatientKey(e) {
  const items = document.querySelectorAll('#patientDrop .ac-item');
  if (e.key === 'ArrowDown') { acFocusIdx = Math.min(acFocusIdx + 1, items.length - 1); items.forEach((el,i) => el.classList.toggle('focused', i === acFocusIdx)); e.preventDefault(); }
  else if (e.key === 'ArrowUp') { acFocusIdx = Math.max(acFocusIdx - 1, 0); items.forEach((el,i) => el.classList.toggle('focused', i === acFocusIdx)); e.preventDefault(); }
  else if (e.key === 'Enter') {
    if (acFocusIdx >= 0 && items[acFocusIdx]) { items[acFocusIdx].click(); }
    else { const val = document.getElementById('patientInp').value.trim(); if (val) selectPatientByName(val, true); }
    e.preventDefault();
  } else if (e.key === 'Escape') { document.getElementById('patientDrop').classList.remove('open'); }
}
function renderAcDrop(matches, val) {
  const items = document.getElementById('patientDropItems');
  const hint = document.getElementById('patientNewHint');
  acFocusIdx = -1;
  if (!matches.length) {
    items.innerHTML = `<div class="ac-item" onclick="selectPatientByName('${esc(val)}',true)"><span class="ac-item-name">${esc(val)}</span><span class="ac-item-count">New patient</span></div>`;
    hint.style.display = 'block';
  } else {
    items.innerHTML = matches.map(p => `
      <div class="ac-item" onclick="selectPatient('${p.id}')">
        <span class="ac-item-name">${esc(p.name)}</span>
        <span class="ac-item-count">${p.formulas.length} formula${p.formulas.length !== 1 ? 's' : ''}</span>
      </div>`).join('');
    hint.style.display = 'none';
  }
  document.getElementById('patientDrop').classList.add('open');
}
function selectPatient(id) {
  const p = PATIENTS.find(x => x.id === id); if (!p) return;
  selPatient = p;
  document.getElementById('patientInp').value = p.name;
  document.getElementById('patientInp').classList.add('has-value');
  document.getElementById('patientClear').style.display = 'block';
  document.getElementById('patientNewBadge').style.display = 'none';
  document.getElementById('patientDrop').classList.remove('open');
  updatePatientHistoryPanel();
}
function selectPatientByName(name, isNew) {
  selPatient = { id: 'new_' + Date.now(), name, formulas: [], isNew: true };
  document.getElementById('patientInp').value = name;
  document.getElementById('patientInp').classList.add('has-value');
  document.getElementById('patientClear').style.display = 'block';
  document.getElementById('patientNewBadge').style.display = isNew ? 'inline-flex' : 'none';
  document.getElementById('patientDrop').classList.remove('open');
  updatePatientHistoryPanel();
}
function clearPatient() {
  selPatient = null;
  const inp = document.getElementById('patientInp');
  inp.value = ''; inp.classList.remove('has-value');
  document.getElementById('patientClear').style.display = 'none';
  document.getElementById('patientNewBadge').style.display = 'none';
  document.getElementById('patientDrop').classList.remove('open');
  updatePatientHistoryPanel();
}
function updatePatientHistoryPanel() {
  const hist = document.getElementById('patientHistory');
  if (!selPatient || !selPatient.formulas.length) { hist.style.display = 'none'; hist.classList.remove('visible'); return; }
  hist.style.display = 'block'; hist.classList.add('visible');
  document.getElementById('patientNameDisplay').textContent = selPatient.name;
  document.getElementById('historyChips').innerHTML = selPatient.formulas.map(f => `
    <div class="formula-chip">
      <span class="chip-type ${f.type}">${f.type === 'cream' ? 'Cream' : f.type === 'compound' ? 'Compound' : 'Herbal'}</span>
      <div class="chip-info">
        <div class="chip-name">${esc(f.name)}</div>
        <div class="chip-meta">${f.summary} &middot; ${formatDate(f.date)}</div>
      </div>
      <button class="chip-load" onclick="loadFormula('${f.id}')">Load &#8599;</button>
    </div>`).join('');
}

/* ── TAB SWITCHING ── */
function switchTab(tab) {
  if (activeTab === tab) return;
  document.querySelectorAll('.tab-content').forEach(el => el.classList.remove('active'));
  document.querySelectorAll('.tab-btn').forEach(el => { el.classList.remove('active','herbal','compound','cream'); });
  document.getElementById('tab-' + tab).classList.add('active');
  const btn = document.getElementById('tab-btn-' + tab);
  btn.classList.add('active', tab);
  activeTab = tab;
  updatePanel(); updateFloatBar(); updatePanelBadge();
}
function updatePanelBadge() {
  const badge = document.getElementById('panelBadge');
  badge.className = 'panel-type-badge ' + activeTab;
  badge.textContent = activeTab === 'cream' ? 'Cream' : activeTab === 'compound' ? 'Compound' : 'Herbal';
}

/* ── LOAD FORMULA (Phase 2 core) ── */
function loadFormula(fid) {
  if (!selPatient) return;
  const f = selPatient.formulas.find(x => x.id === fid); if (!f) return;
  switchTab(f.type);
  const dateStr = formatDate(f.date);
  if (f.type === 'herbal') {
    HS = { bottle: f.data.bottle, bottleMax: f.data.bottle === 200 ? 215 : 525, material: f.data.material || 'glass',
           dropper: f.data.dropper || false, qtys: {...f.data.qtys}, essenceQtys: {...f.data.essenceQtys}, loaded: f };
    document.getElementById('herb-formulaName').value = f.name;
    document.getElementById('herb-dosage').value = f.data.dosage || '';
    setBottleUI();
    renderHerbTable(); renderHerbEssences();
    showLoaded('herb', `${f.name} · dispensed ${dateStr} · Adjust as needed before adding to cart`);
  } else if (f.type === 'compound') {
    CS = { duration: f.data.duration, doses: {...f.data.doses}, loaded: f };
    document.getElementById('comp-formulaName').value = f.name;
    document.getElementById('comp-dosage').value = f.data.dosage || '';
    const durInp = document.getElementById('comp-durInp');
    durInp.value = f.data.duration; durInp.classList.remove('empty');
    onDurChange(false); renderCompTable();
    showLoaded('comp', `${f.name} · dispensed ${dateStr} · Adjust as needed before adding to cart`);
  } else if (f.type === 'cream') {
    KS = { base: f.data.base || null, qtys: {...f.data.qtys}, essenceQtys: {...f.data.essenceQtys}, loaded: f };
    document.getElementById('cream-formulaName').value = f.name;
    document.getElementById('cream-dosage').value = f.data.dosage || '';
    renderCreamBases(); renderCreamTable(); renderCreamEssences();
    showLoaded('cream', `${f.name} · dispensed ${dateStr} · Adjust as needed before adding to cart`);
  }
  updatePanel(); updateFloatBar();
}
function showLoaded(pfx, msg) {
  const b = document.getElementById(pfx + '-loadedBanner'), t = document.getElementById(pfx + '-loadedText');
  if (b && t) { t.textContent = msg; b.classList.add('visible'); }
}
function clearLoaded(tab) {
  const pfx = tab === 'herbal' ? 'herb' : tab === 'compound' ? 'comp' : 'cream';
  const b = document.getElementById(pfx + '-loadedBanner'); if (b) b.classList.remove('visible');
  if (tab === 'herbal') HS.loaded = null;
  if (tab === 'compound') CS.loaded = null;
  if (tab === 'cream') KS.loaded = null;
}

/* ── HERBAL BUILDER ── */
function selectBottle(card) {
  document.querySelectorAll('#tab-herbal .bottle-card').forEach(c => c.classList.remove('sel'));
  card.classList.add('sel');
  HS.bottle = parseInt(card.dataset.size);
  HS.bottleMax = parseInt(card.dataset.max);
  HS.qtys = {}; HS.essenceQtys = {}; HS.dropper = false;
  setBottleUI(); renderHerbTable(); renderHerbEssences(); updatePanel(); updateFloatBar();
}
function setBottleUI() {
  const dOpt = document.getElementById('herb-dropperOpt'), mOpt = document.getElementById('herb-matOpt');
  const banner = document.getElementById('herb-noBotBanner');
  if (!HS.bottle) { if (banner) banner.style.display = 'block'; return; }
  if (dOpt) dOpt.classList.toggle('visible', HS.bottle === 200);
  if (mOpt) mOpt.classList.toggle('visible', HS.bottle === 500);
  if (banner) banner.style.display = 'none';
  document.querySelectorAll('#tab-herbal .bottle-card').forEach(c => {
    c.classList.toggle('sel', parseInt(c.dataset.size) === HS.bottle);
  });
  document.getElementById('herb-matGlass')?.classList.toggle('active', HS.material === 'glass');
  document.getElementById('herb-matPlastic')?.classList.toggle('active', HS.material === 'plastic');
  const chk = document.getElementById('herb-dropperChk'); if (chk) chk.checked = HS.dropper;
}
function selectMat(m) {
  HS.material = m;
  document.getElementById('herb-matGlass').classList.toggle('active', m === 'glass');
  document.getElementById('herb-matPlastic').classList.toggle('active', m === 'plastic');
}
function updateHerbPanel() { HS.dropper = document.getElementById('herb-dropperChk').checked; updatePanel(); }

function herbRowHtml(h) {
  const q = HS.qtys[h.id] || 0, locked = !HS.bottle;
  const inp = locked
    ? '<span style="font-size:11px;color:var(--text-light);font-style:italic">Select container</span>'
    : `<div class="qty-wrap"><input class="qty-inp" type="number" min="0" id="hqi${h.id}" value="${q || ''}" placeholder="0" oninput="setHerbQty(${h.id},parseInt(this.value)||0)"/><span class="qty-unit">mL</span></div>`;
  return `<tr id="hrow${h.id}" class="${q > 0 ? 'hq' : ''}">
    <td style="text-align:center"><div class="ing-img">${h.image ? `<img src="${esc(h.image)}" alt="" loading="lazy"/>` : '&#127807;'}</div></td>
    <td style="min-width:180px"><button class="ing-lnk" onclick="openHerbDrawer(${h.id},'herbal')">${esc(h.name)}</button>${newBadge(h)}<div class="sub-nm">${esc(h.vendor)}</div></td>
    <td style="font-size:11px;color:var(--text-muted)">${esc(h.dosage)}</td>
    <td style="font-size:12px;color:var(--text-muted)">${h.price !== null ? '$' + h.price.toFixed(2) : '—'}</td>
    <td>${stockCell(h.stock)}</td>
    <td>${inp}</td>
  </tr>`;
}
function renderHerbTable() {
  const s = (document.getElementById('herb-si')?.value || '').toLowerCase();
  const clr = document.getElementById('herb-siClr'); if (clr) clr.style.display = s ? 'block' : 'none';
  const list = HERBS.filter(h => !s || [h.name, h.description, h.vendor, h.dosage].some(f => f && f.toLowerCase().includes(s)));
  const tb = document.getElementById('herb-tbody'); if (!tb) return;
  const nr = document.getElementById('herb-nores');
  if (!list.length && dataLoaded) { tb.innerHTML = ''; if (nr) nr.style.display = 'block'; return; }
  if (nr) nr.style.display = 'none';
  tb.innerHTML = list.map(h => herbRowHtml(h)).join('');
}
function setHerbQty(id, v) {
  if (!HS.bottle) return;
  const other = Object.entries(HS.qtys).filter(([k]) => parseInt(k) !== id).reduce((s,[,val]) => s + val, 0);
  v = Math.max(0, Math.min(v, HS.bottleMax - other));
  if (v === 0) delete HS.qtys[id]; else HS.qtys[id] = v;
  const inp = document.getElementById('hqi' + id); if (inp) inp.value = v || '';
  const row = document.getElementById('hrow' + id); if (row) row.classList.toggle('hq', v > 0);
  if (drawerItem?.id === id && drawerTab === 'herbal') { document.getElementById('dQty').value = v; document.getElementById('dMinus').disabled = v === 0; }
  renderHerbEssences(); updatePanel(); updateFloatBar();
}

/* ── ESSENCES ── */
function renderHerbEssences() {
  const hasHerbs = Object.values(HS.qtys).some(v => v > 0);
  const body = document.getElementById('herb-ess-body'), num = document.getElementById('herb-ess-num');
  if (!hasHerbs) { body.innerHTML = '<div class="lock-msg">&#8593; Add at least one herb to unlock.</div>'; num.classList.add('dim'); return; }
  num.classList.remove('dim');
  renderEssenceGrid(body, HS.essenceQtys, 'herb');
}
function renderCreamEssences() {
  const hasHerbs = Object.values(KS.qtys).some(v => v > 0);
  const body = document.getElementById('cream-ess-body'), num = document.getElementById('cream-ess-num');
  if (!hasHerbs) { body.innerHTML = '<div class="lock-msg">&#8593; Add at least one herb to unlock.</div>'; num.classList.add('dim'); return; }
  num.classList.remove('dim');
  renderEssenceGrid(body, KS.essenceQtys, 'cream');
}
function renderEssenceGrid(body, essQtys, pfx) {
  const tot = Object.values(essQtys).reduce((s, v) => s + v, 0), rem = ESS_MAX - tot;
  let html = `<div class="step-body"><p style="font-size:12px;color:var(--text-muted);margin-bottom:10px">${tot}/${ESS_MAX} doses used &middot; ${rem} remaining</p><div class="essences-row">`;
  ESSENCES.forEach(e => {
    const q = essQtys[e.id] || 0;
    html += `<div class="ess-card ${q > 0 ? 'has-qty' : ''}">
      <div class="ess-icon">${e.image ? `<img src="${esc(e.image)}" alt="" style="width:100%;height:100%;object-fit:cover;border-radius:4px"/>` : '&#127804;'}</div>
      <div class="ess-name" onclick="openEssDrawer(${e.id},'${pfx}')">${esc(e.name)}</div>
      <div class="ess-price">${e.price !== null ? '$' + e.price.toFixed(2) + '/dose' : '—'}</div>
      <div class="ess-qty">
        <button class="ess-btn" onclick="adjEss(${e.id},'${pfx}',-1)" ${q === 0 ? 'disabled' : ''}>&minus;</button>
        <input class="ess-inp" type="number" min="0" value="${q}" id="${pfx}ess${e.id}" onchange="setEss(${e.id},'${pfx}',parseInt(this.value)||0)"/>
        <button class="ess-btn" onclick="adjEss(${e.id},'${pfx}',1)" ${rem <= 0 && q === 0 ? 'disabled' : ''}>+</button>
      </div>
    </div>`;
  });
  body.innerHTML = html + '</div></div>';
}
function adjEss(id, pfx, d) {
  const map = pfx === 'herb' ? HS.essenceQtys : KS.essenceQtys;
  const c = map[id] || 0, tot = Object.values(map).reduce((s,v) => s + v, 0);
  const nv = d > 0 ? Math.min(c + 1, c + Math.min(1, ESS_MAX - tot)) : Math.max(0, c - 1);
  setEss(id, pfx, nv);
}
function setEss(id, pfx, v) {
  const map = pfx === 'herb' ? HS.essenceQtys : KS.essenceQtys;
  const otherTot = Object.entries(map).filter(([k]) => parseInt(k) !== id).reduce((s,[,val]) => s + val, 0);
  v = Math.max(0, Math.min(v, ESS_MAX - otherTot));
  if (v === 0) delete map[id]; else map[id] = v;
  const inp = document.getElementById(pfx + 'ess' + id); if (inp) inp.value = v;
  if (pfx === 'herb') renderHerbEssences(); else renderCreamEssences();
  updatePanel();
}

/* ── COMPOUND BUILDER ── */
function getDur() { return Math.max(0, parseFloat(document.getElementById('comp-durInp')?.value) || 0); }
function totalCompGrams(id) { return rnd((CS.doses[id] || 0) * getDur(), 3); }
function onDurChange(updateTable = true) {
  const dur = getDur(), inp = document.getElementById('comp-durInp'), banner = document.getElementById('comp-durBanner');
  if (inp) inp.classList.toggle('empty', !dur);
  if (banner) banner.style.display = dur ? 'none' : 'block';
  CS.duration = dur;
  document.querySelectorAll('.comp-dose-inp').forEach(el => el.disabled = !dur);
  const sel = Object.entries(CS.doses).filter(([,v]) => v > 0);
  const totalDaily = rnd(sel.reduce((s,[,v]) => s + v, 0));
  const totalWeight = dur ? rnd(sel.reduce((s,[id,d]) => s + rnd(d * dur, 3), 0)) : 0;
  const showVal = (v, u) => (dur && sel.length) ? `${v}${u}` : '—';
  document.getElementById('comp-totDaily').textContent = showVal(totalDaily, 'g/day');
  document.getElementById('comp-totWeight').textContent = showVal(totalWeight, 'g');
  if (updateTable) COMPOUNDS.forEach(ing => { const cell = document.getElementById('ctg' + ing.id); if (cell) cell.innerHTML = totalCompGramsCell(ing.id); });
  updatePanel(); updateFloatBar();
}
function totalCompGramsCell(id) {
  const d = CS.doses[id] || 0, dur = getDur();
  if (!d || !dur) return '<span class="total-g zero">—</span>';
  const tg = totalCompGrams(id);
  const frac = !isWhole(tg) ? '<span style="font-size:10px;color:var(--warn-amber);display:block">note fraction</span>' : '';
  return `<span class="total-g">${tg}g</span>${frac}`;
}
function compRowHtml(ing) {
  const d = CS.doses[ing.id] || 0, dur = getDur();
  const icons = (ing.isMulti ? `<span class="tt-wrap"><span class="tt-icon multi">&#9432;</span><span class="tt-text">Multi-ingredient. Review drawer for full breakdown before combining.</span></span>` : '') +
    (ing.warnings ? `<span class="tt-wrap"><span class="tt-icon warn">!</span><span class="tt-text">${esc(ing.warnings)}</span></span>` : '');
  const elemIcon = ing.elemental ? `<span class="tt-wrap"><span class="tt-icon elem">&#9432;</span><span class="tt-text">${esc(ing.elemental)}</span></span>` : '';
  return `<tr id="crow${ing.id}" class="${d > 0 ? 'hq' : ''}">
    <td style="text-align:center"><div class="ing-img">${ing.image ? `<img src="${esc(ing.image)}" alt="" loading="lazy"/>` : '&#128300;'}</div></td>
    <td style="min-width:175px"><button class="ing-lnk" onclick="openCompDrawer(${ing.id})">${esc(ing.name)}</button>${newBadge(ing)}${icons}<div class="sub-nm">${esc(ing.vendor)}</div></td>
    <td style="font-size:12px;color:var(--text-muted)">${esc(ing.recDose)}${elemIcon}</td>
    <td style="font-size:12px;color:var(--text-muted)">${ing.price !== null ? '$' + ing.price.toFixed(2) : '—'}</td>
    <td>${stockCell(ing.stock, COMP_LOW_STOCK)}</td>
    <td><div class="qty-wrap"><input class="qty-inp comp-dose-inp" type="text" inputmode="decimal" id="cdi${ing.id}" value="${d || ''}" placeholder="0" ${!dur ? 'disabled' : ''} oninput="setCompDose(${ing.id},parseFloat(this.value)||0)"/><span class="qty-unit">g</span></div></td>
    <td id="ctg${ing.id}">${totalCompGramsCell(ing.id)}</td>
  </tr>`;
}
function renderCompTable() {
  const s = (document.getElementById('comp-si')?.value || '').toLowerCase();
  const clr = document.getElementById('comp-siClr'); if (clr) clr.style.display = s ? 'block' : 'none';
  const list = COMPOUNDS.filter(ing => !s || [ing.name, ing.description, ing.vendor].some(f => f && f.toLowerCase().includes(s)));
  const tb = document.getElementById('comp-tbody'); if (!tb) return;
  const nr = document.getElementById('comp-nores');
  if (!list.length && dataLoaded) { tb.innerHTML = ''; if (nr) nr.style.display = 'block'; return; }
  if (nr) nr.style.display = 'none';
  tb.innerHTML = list.map(ing => compRowHtml(ing)).join('');
}
function setCompDose(id, val) {
  if (!getDur()) return;
  val = Math.max(0, rnd(val, 4));
  if (val === 0) delete CS.doses[id]; else CS.doses[id] = val;
  const inp = document.getElementById('cdi' + id); if (inp) inp.value = val || '';
  const row = document.getElementById('crow' + id); if (row) row.classList.toggle('hq', val > 0);
  const cell = document.getElementById('ctg' + id); if (cell) cell.innerHTML = totalCompGramsCell(id);
  if (drawerItem?.id === id && drawerTab === 'compound') { document.getElementById('dQty').value = val || 0; document.getElementById('dMinus').disabled = val === 0; }
  onDurChange(false);
}

/* ── CREAM BUILDER ── */
function renderCreamBases() {
  const grid = document.getElementById('cream-baseGrid'); if (!grid) return;
  if (!dataLoaded) { grid.innerHTML = '<div class="lock-msg">Loading cream bases&hellip;</div>'; return; }
  if (!CREAM_BASES.length) { grid.innerHTML = '<div class="lock-msg">No cream bases available.</div>'; return; }
  grid.innerHTML = CREAM_BASES.map(b => `
    <div class="base-card ${KS.base === b.id ? 'sel' : ''}" onclick="selectBase(${b.id})">
      <div class="base-tick">&#10003;</div>
      <div class="b-icon">${b.image ? `<img src="${esc(b.image)}" alt="" style="width:32px;height:32px;object-fit:cover;border-radius:4px"/>` : '&#129337;'}</div>
      <div class="b-name">${esc(b.name)}</div>
      <div class="b-detail">${b.price !== null ? '$' + b.price.toFixed(2) : ''}</div>
    </div>`).join('');
}
function selectBase(id) { KS.base = id; renderCreamBases(); updatePanel(); }

function creamRowHtml(h) {
  const q = KS.qtys[h.id] || 0;
  return `<tr id="krow${h.id}" class="${q > 0 ? 'hq' : ''}">
    <td style="text-align:center"><div class="ing-img">${h.image ? `<img src="${esc(h.image)}" alt="" loading="lazy"/>` : '&#127807;'}</div></td>
    <td style="min-width:180px"><button class="ing-lnk" onclick="openHerbDrawer(${h.id},'cream')">${esc(h.name)}</button>${newBadge(h)}<div class="sub-nm">${esc(h.vendor)}</div></td>
    <td style="font-size:11px;color:var(--text-muted)">${esc(h.dosage)}</td>
    <td style="font-size:12px;color:var(--text-muted)">${h.price !== null ? '$' + h.price.toFixed(2) : '—'}</td>
    <td>${stockCell(h.stock)}</td>
    <td><div class="qty-wrap"><input class="qty-inp" type="number" min="0" id="kqi${h.id}" value="${q || ''}" placeholder="0" oninput="setCreamQty(${h.id},parseInt(this.value)||0)"/><span class="qty-unit">mL</span></div></td>
  </tr>`;
}
function renderCreamTable() {
  const s = (document.getElementById('cream-si')?.value || '').toLowerCase();
  const clr = document.getElementById('cream-siClr'); if (clr) clr.style.display = s ? 'block' : 'none';
  const list = HERBS.filter(h => !s || [h.name, h.description, h.vendor, h.dosage].some(f => f && f.toLowerCase().includes(s)));
  const tb = document.getElementById('cream-tbody'); if (!tb) return;
  const nr = document.getElementById('cream-nores');
  if (!list.length && dataLoaded) { tb.innerHTML = ''; if (nr) nr.style.display = 'block'; return; }
  if (nr) nr.style.display = 'none';
  tb.innerHTML = list.map(h => creamRowHtml(h)).join('');
}
function setCreamQty(id, v) {
  v = Math.max(0, v);
  if (v === 0) delete KS.qtys[id]; else KS.qtys[id] = v;
  const inp = document.getElementById('kqi' + id); if (inp) inp.value = v || '';
  const row = document.getElementById('krow' + id); if (row) row.classList.toggle('hq', v > 0);
  if (drawerItem?.id === id && drawerTab === 'cream') { document.getElementById('dQty').value = v; document.getElementById('dMinus').disabled = v === 0; }
  renderCreamEssences(); updatePanel(); updateFloatBar();
}

/* ── REVIEW PANEL ── */
function updatePanel() {
  const tab = activeTab;
  const pe = document.getElementById('panelEmpty'), pc = document.getElementById('panelContent');
  const cl = document.getElementById('chipList'), ps = document.getElementById('panelSumm');
  const ab = document.getElementById('panelAtc'), cb = document.getElementById('clearBtn');

  let items = [], cost = 0, summRows = [], hasContent = false;

  if (tab === 'herbal') {
    const sel = Object.entries(HS.qtys).filter(([,v]) => v > 0);
    hasContent = sel.length > 0;
    items = sel.map(([id, ml]) => {
      const h = HERBS.find(x => x.id === parseInt(id)); if (!h) return null;
      const c = h.price !== null ? h.price * ml : 0; cost += c;
      return { name: h.name, qty: `${ml}mL`, cost: c, removeId: parseInt(id), removeType: 'herb' };
    }).filter(Boolean);
    Object.entries(HS.essenceQtys).filter(([,v]) => v > 0).forEach(([id, doses]) => {
      const e = ESSENCES.find(x => x.id === parseInt(id)); if (!e) return;
      const c = e.price !== null ? e.price * doses : 0; cost += c;
      items.push({ name: e.name, qty: `${doses} dose${doses > 1 ? 's' : ''}`, cost: c, removeId: parseInt(id), removeType: 'ess-herb' });
    });
    if (HS.dropper) { cost += DROPPER_PRICE; items.push({ name: 'Glass Dropper', qty: '×1', cost: DROPPER_PRICE, removeId: null }); }
    const tot = sel.reduce((s,[,v]) => s + v, 0);
    summRows = [
      { label: 'Container', val: HS.bottle ? `${HS.bottle}mL (${HS.material})` : '—' },
      { label: 'Volume', val: HS.bottle ? `${tot}/${HS.bottleMax}mL` : '—', blue: true },
      { label: 'Price', val: `$${cost.toFixed(2)}`, tot: true },
    ];
  } else if (tab === 'compound') {
    const dur = getDur(), sel = Object.entries(CS.doses).filter(([,v]) => v > 0);
    hasContent = sel.length > 0;
    const totalDaily = rnd(sel.reduce((s,[,v]) => s + v, 0));
    const totalWeight = dur ? rnd(sel.reduce((s,[id,d]) => s + rnd(d * dur, 3), 0)) : 0;
    items = sel.map(([id, d]) => {
      const ing = COMPOUNDS.find(x => x.id === parseInt(id)); if (!ing) return null;
      const tg = dur ? rnd(d * dur, 3) : null; const c = (tg && ing.price !== null) ? ing.price * tg : 0; cost += c;
      return { name: ing.name, qty: dur ? `${d}g/day · ${tg}g` : `${d}g/day`, cost: c, removeId: parseInt(id), removeType: 'comp' };
    }).filter(Boolean);
    summRows = [
      { label: 'Duration', val: dur ? `${dur} days` : '—' },
      { label: 'Daily dose', val: dur && sel.length ? `${totalDaily}g/day` : '—', blue: true },
      { label: 'Total weight', val: dur && sel.length ? `${totalWeight}g` : '—', blue: true },
      { label: 'Price', val: `$${cost.toFixed(2)}`, tot: true },
    ];
  } else if (tab === 'cream') {
    const sel = Object.entries(KS.qtys).filter(([,v]) => v > 0);
    hasContent = sel.length > 0 || KS.base !== null;
    const base = CREAM_BASES.find(b => b.id === KS.base);
    if (base) { const c = base.price || 0; cost += c; items.push({ name: base.name + ' (base)', qty: '×1', cost: c, removeId: null }); }
    sel.forEach(([id, ml]) => {
      const h = HERBS.find(x => x.id === parseInt(id)); if (!h) return;
      const c = h.price !== null ? h.price * ml : 0; cost += c;
      items.push({ name: h.name, qty: `${ml}mL`, cost: c, removeId: parseInt(id), removeType: 'cream' });
    });
    Object.entries(KS.essenceQtys).filter(([,v]) => v > 0).forEach(([id, doses]) => {
      const e = ESSENCES.find(x => x.id === parseInt(id)); if (!e) return;
      const c = e.price !== null ? e.price * doses : 0; cost += c;
      items.push({ name: e.name, qty: `${doses} dose${doses > 1 ? 's' : ''}`, cost: c, removeId: parseInt(id), removeType: 'ess-cream' });
    });
    const totMl = sel.reduce((s,[,v]) => s + v, 0);
    summRows = [
      { label: 'Base', val: base ? base.name : '—' },
      { label: 'Herbs', val: `${totMl}mL`, blue: true },
      { label: 'Price', val: `$${cost.toFixed(2)}`, tot: true },
    ];
  }

  if (!hasContent) {
    pe.style.display = 'block'; pc.style.display = 'none';
    if (ab && ab.tagName === 'BUTTON') ab.disabled = true;
    if (cb) cb.style.display = 'none';
    return;
  }
  pe.style.display = 'none'; pc.style.display = 'block';
  if (ab && ab.tagName === 'BUTTON') ab.disabled = false;
  if (cb) cb.style.display = 'block';

  const LIMIT = 4, exp = chipExpanded[tab];
  const visible = exp ? items : items.slice(0, LIMIT);
  let chips = visible.map(item => `
    <div class="chip-row">
      <span class="chip-nm">${esc(item.name)}</span>
      <span class="chip-qty">${item.qty}</span>
      <span class="chip-cost">$${item.cost.toFixed(2)}</span>
      ${item.removeId !== null && item.removeId !== undefined ? `<button class="chip-x" onclick="removeItem(${typeof item.removeId === 'string' ? "'"+item.removeId+"'" : item.removeId},'${item.removeType || ''}')">&#10005;</button>` : ''}
    </div>`).join('');
  if (items.length > LIMIT) {
    chips += exp
      ? `<button class="chip-toggle" onclick="chipExpanded['${tab}']=false;updatePanel()">Show less &#8593;</button>`
      : `<button class="chip-toggle" onclick="chipExpanded['${tab}']=true;updatePanel()">Show all ${items.length} &#8594;</button>`;
  }
  cl.innerHTML = chips;
  ps.innerHTML = summRows.map(r => `<div class="summ-row ${r.tot ? 'tot' : r.blue ? 'blue' : ''}"><span>${r.label}</span><span>${r.val}</span></div>`).join('');

  if (cartCount > 0) {
    document.getElementById('cartTally').style.display = 'block';
    document.getElementById('cartCount').textContent = cartCount;
    document.getElementById('viewCartBtn').style.display = 'block';
  }
}
function removeItem(id, type) {
  if (type === 'herb') setHerbQty(parseInt(id), 0);
  else if (type === 'comp') setCompDose(parseInt(id), 0);
  else if (type === 'cream') setCreamQty(parseInt(id), 0);
  else if (type === 'ess-herb') setEss(id, 'herb', 0);
  else if (type === 'ess-cream') setEss(id, 'cream', 0);
  updatePanel();
}

/* ── FLOATING BAR ── */
function updateFloatBar() {
  const tab = activeTab;
  const progTrack = document.getElementById('fl-progTrack'), progFill = document.getElementById('fl-progFill');
  const volStat = document.getElementById('fl-volStat'), weightStat = document.getElementById('fl-weightStat');
  const flDiv = document.getElementById('fl-divProg'), flDivW = document.getElementById('fl-divWeight');
  const flAtc = document.getElementById('fl-atc');

  if (tab === 'herbal') {
    if (progTrack) progTrack.style.display = 'block'; if (volStat) volStat.style.display = 'flex'; if (flDiv) flDiv.style.display = 'block';
    if (weightStat) weightStat.style.display = 'none'; if (flDivW) flDivW.style.display = 'none';
    const tot = Object.values(HS.qtys).reduce((s,v) => s + v, 0);
    const pct = HS.bottle ? Math.min(100, (tot / HS.bottleMax) * 100) : 0;
    if (progFill) { progFill.style.width = pct + '%'; progFill.className = 'prog-fill' + (pct >= 100 ? ' full' : pct >= 80 ? ' warn' : ''); }
    const vol = document.getElementById('fl-vol'); if (vol) vol.textContent = HS.bottle ? `${tot}/${HS.bottleMax}mL` : '—';
    document.getElementById('fl-itemsLbl').textContent = 'Herbs';
    document.getElementById('fl-items').textContent = Object.values(HS.qtys).filter(v => v > 0).length + ' selected';
    if (flAtc && flAtc.tagName === 'BUTTON') flAtc.disabled = !Object.values(HS.qtys).some(v => v > 0);
  } else if (tab === 'compound') {
    if (progTrack) progTrack.style.display = 'none'; if (volStat) volStat.style.display = 'none'; if (flDiv) flDiv.style.display = 'none';
    if (weightStat) weightStat.style.display = 'flex'; if (flDivW) flDivW.style.display = 'block';
    const dur = getDur(), sel = Object.entries(CS.doses).filter(([,v]) => v > 0);
    const totalWeight = dur ? rnd(sel.reduce((s,[id,d]) => s + rnd(d * dur, 3), 0)) : 0;
    const wt = document.getElementById('fl-weight'); if (wt) wt.textContent = dur && sel.length ? `${totalWeight}g` : '—';
    document.getElementById('fl-itemsLbl').textContent = 'Ingredients';
    document.getElementById('fl-items').textContent = sel.length + ' selected';
    if (flAtc && flAtc.tagName === 'BUTTON') flAtc.disabled = sel.length === 0 || !dur;
  } else if (tab === 'cream') {
    if (progTrack) progTrack.style.display = 'none'; if (volStat) volStat.style.display = 'none'; if (flDiv) flDiv.style.display = 'none';
    if (weightStat) weightStat.style.display = 'none'; if (flDivW) flDivW.style.display = 'none';
    document.getElementById('fl-itemsLbl').textContent = 'Herbs';
    document.getElementById('fl-items').textContent = Object.values(KS.qtys).filter(v => v > 0).length + ' selected';
    if (flAtc && flAtc.tagName === 'BUTTON') flAtc.disabled = !Object.values(KS.qtys).some(v => v > 0) && !KS.base;
  }
}

/* ── CART CONFIRMATION ── */
function confirmAddToCart() {
  const tab = activeTab;
  const patientName = document.getElementById('patientInp').value.trim();
  if (!patientName) { showToast('Please enter a patient name first.'); document.getElementById('patientInp').focus(); return; }

  let formulaName = '', dosage = '', details = [];
  if (tab === 'herbal') {
    formulaName = document.getElementById('herb-formulaName').value.trim();
    dosage = document.getElementById('herb-dosage').value.trim();
    if (!formulaName) { showToast('Please enter a formula name.'); document.getElementById('herb-formulaName').focus(); return; }
    if (!dosage) { showToast('Please enter dosage instructions.'); document.getElementById('herb-dosage').focus(); return; }
    if (!HS.bottle) { showToast('Please select a container.'); return; }
    const sel = Object.entries(HS.qtys).filter(([,v]) => v > 0);
    if (!sel.length) { showToast('Please add at least one herb.'); return; }
    const tot = sel.reduce((s,[,v]) => s + v, 0);
    details = [
      { label: 'Patient', val: patientName },
      { label: 'Formula', val: formulaName },
      { label: 'Dosage', val: dosage },
      { label: 'Container', val: `${HS.bottle}mL ${HS.material}` },
      { label: 'Volume', val: `${tot}/${HS.bottleMax}mL · ${sel.length} herb${sel.length !== 1 ? 's' : ''}` },
    ];
  } else if (tab === 'compound') {
    formulaName = document.getElementById('comp-formulaName').value.trim();
    dosage = document.getElementById('comp-dosage').value.trim();
    if (!formulaName) { showToast('Please enter a formula name.'); document.getElementById('comp-formulaName').focus(); return; }
    if (!dosage) { showToast('Please enter dosage instructions.'); document.getElementById('comp-dosage').focus(); return; }
    const dur = getDur();
    if (!dur) { showToast('Please enter treatment duration.'); document.getElementById('comp-durInp').focus(); return; }
    const sel = Object.entries(CS.doses).filter(([,v]) => v > 0);
    if (!sel.length) { showToast('Please add at least one ingredient.'); return; }
    const totalWeight = rnd(sel.reduce((s,[id,d]) => s + rnd(d * dur, 3), 0));
    details = [
      { label: 'Patient', val: patientName },
      { label: 'Formula', val: formulaName },
      { label: 'Dosage', val: dosage },
      { label: 'Duration', val: `${dur} days` },
      { label: 'Weight', val: `${totalWeight}g · ${sel.length} ingredient${sel.length !== 1 ? 's' : ''}` },
    ];
  } else if (tab === 'cream') {
    formulaName = document.getElementById('cream-formulaName').value.trim();
    dosage = document.getElementById('cream-dosage').value.trim();
    if (!formulaName) { showToast('Please enter a formula name.'); document.getElementById('cream-formulaName').focus(); return; }
    if (!dosage) { showToast('Please enter application instructions.'); document.getElementById('cream-dosage').focus(); return; }
    if (!KS.base) { showToast('Please select a cream base.'); return; }
    const base = CREAM_BASES.find(b => b.id === KS.base);
    const sel = Object.entries(KS.qtys).filter(([,v]) => v > 0);
    details = [
      { label: 'Patient', val: patientName },
      { label: 'Formula', val: formulaName },
      { label: 'Instructions', val: dosage },
      { label: 'Base', val: base?.name || '—' },
      { label: 'Herbs', val: `${sel.length} selected` },
    ];
  }

  document.getElementById('cartModalDetail').innerHTML = details.map(d =>
    `<div class="modal-detail-row"><span class="lbl">${d.label}</span><span class="val">${esc(d.val)}</span></div>`).join('');
  openModal('cartModal');
}

async function doAddToCart() {
  closeModal('cartModal');
  const tab = activeTab, cfg = window.PB_CONFIG;
  const patientName = document.getElementById('patientInp').value.trim();
  let formulaName = '', dosage = '', sp = '';

  if (tab === 'herbal') {
    formulaName = document.getElementById('herb-formulaName').value.trim();
    dosage = document.getElementById('herb-dosage').value.trim();
    sp = document.getElementById('herb-sp').value.trim();
  } else if (tab === 'compound') {
    formulaName = document.getElementById('comp-formulaName').value.trim();
    dosage = document.getElementById('comp-dosage').value.trim();
    sp = document.getElementById('comp-sp').value.trim();
  } else {
    formulaName = document.getElementById('cream-formulaName').value.trim();
    dosage = document.getElementById('cream-dosage').value.trim();
    sp = document.getElementById('cream-sp').value.trim();
  }

  const dispenserNotes = document.getElementById('panelNotes')?.value.trim() || '';
  const formulaId = Date.now().toString(36) + Math.random().toString(36).slice(2, 5);
  let items = [];

  if (tab === 'herbal') {
    const sel = Object.entries(HS.qtys).filter(([,v]) => v > 0);
    const totMl = sel.reduce((s,[,v]) => s + v, 0);
    let containerVariantId = HS.bottle === 200 ? cfg.bottle200VariantId : (HS.material === 'glass' ? cfg.bottle500Variants.glass : cfg.bottle500Variants.plastic);
    if (!containerVariantId) { showToast('Product not yet configured. Please contact the store admin.'); return; }
    const containerProps = {
      '_formula_id': formulaId,
      'Name for label': patientName,
      'Formula name': formulaName,
      'Dosage instructions': dosage,
      'Total volume': `${totMl}mL`
    };
    if (sp) containerProps['Special requests'] = sp;
    if (dispenserNotes) containerProps['Dispenser notes'] = dispenserNotes;
    items.push({ id: containerVariantId, quantity: 1, properties: containerProps });
    if (HS.dropper && cfg.dropperVariantId) items.push({ id: cfg.dropperVariantId, quantity: 1, properties: { '_formula_id': formulaId } });
    for (const [id, ml] of sel) {
      const h = HERBS.find(x => x.id === parseInt(id));
      if (h) items.push({ id: h.variantId, quantity: ml, properties: { '_formula_id': formulaId } });
    }
    for (const [id, doses] of Object.entries(HS.essenceQtys).filter(([,v]) => v > 0)) {
      const e = ESSENCES.find(x => x.id === parseInt(id));
      if (e) items.push({ id: e.variantId, quantity: doses, properties: { '_formula_id': formulaId } });
    }
  } else if (tab === 'compound') {
    if (!cfg.compoundTubVariantId) { showToast('Product not yet configured. Please contact the store admin.'); return; }
    const dur = getDur(), sel = Object.entries(CS.doses).filter(([,v]) => v > 0);
    const totalDaily = rnd(sel.reduce((s,[,v]) => s + v, 0));
    const totalWeight = rnd(sel.reduce((s,[id,d]) => s + rnd(d * dur, 3), 0));
    const containerProps = {
      '_formula_id': formulaId,
      '_container': 'Plastic Compound Tub',
      'Name for label': patientName,
      'Formula name': formulaName,
      'Dosage instructions': dosage,
      'Treatment duration': `${dur} days`,
      '_calculated_daily': `${totalDaily}g/day`,
      '_calculated_weight': `${totalWeight}g`
    };
    if (sp) containerProps['Special requests'] = sp;
    if (dispenserNotes) containerProps['Dispenser notes'] = dispenserNotes;
    const tubQty = Math.max(1, Math.round(totalWeight));
    items.push({ id: cfg.compoundTubVariantId, quantity: tubQty, properties: containerProps });
    for (const [id, dose] of sel) {
      const ing = COMPOUNDS.find(x => x.id === parseInt(id));
      if (ing) items.push({ id: ing.variantId, quantity: Math.max(1, Math.round(rnd(dose * dur, 3))), properties: { '_formula_id': formulaId } });
    }
  } else if (tab === 'cream') {
    if (!cfg.creamJarVariantId) { showToast('Product not yet configured. Please contact the store admin.'); return; }
    const sel = Object.entries(KS.qtys).filter(([,v]) => v > 0);
    const totMl = sel.reduce((s,[,v]) => s + v, 0);
    const base = CREAM_BASES.find(b => b.id === KS.base);
    const containerProps = {
      '_formula_id': formulaId,
      '_container': 'Cream base (no bottle)',
      'Name for label': patientName,
      'Formula name': formulaName,
      'Application instructions': dosage,
      'Cream base': base?.name || '—',
      'Total herb volume': `${totMl}mL`
    };
    if (sp) containerProps['Special requests'] = sp;
    if (dispenserNotes) containerProps['Dispenser notes'] = dispenserNotes;
    items.push({ id: cfg.creamJarVariantId, quantity: 1, properties: containerProps });
    if (base) items.push({ id: base.variantId, quantity: 1, properties: { '_formula_id': formulaId } });
    for (const [id, ml] of sel) {
      const h = HERBS.find(x => x.id === parseInt(id));
      if (h) items.push({ id: h.variantId, quantity: ml, properties: { '_formula_id': formulaId } });
    }
    for (const [id, doses] of Object.entries(KS.essenceQtys).filter(([,v]) => v > 0)) {
      const e = ESSENCES.find(x => x.id === parseInt(id));
      if (e) items.push({ id: e.variantId, quantity: doses, properties: { '_formula_id': formulaId } });
    }
  }

  if (!items.length) return;

  const panelAtc = document.getElementById('panelAtc'), flAtc = document.getElementById('fl-atc');
  const cartConfirmBtn = document.getElementById('cartModalConfirm');
  const setLoading = (on) => {
    if (panelAtc && panelAtc.tagName === 'BUTTON') { panelAtc.disabled = on; panelAtc.textContent = on ? 'Adding…' : 'Add to Cart'; }
    if (flAtc && flAtc.tagName === 'BUTTON') { flAtc.disabled = on; flAtc.textContent = on ? 'Adding…' : 'Add to Cart'; }
    if (cartConfirmBtn) cartConfirmBtn.disabled = on;
  };

  setLoading(true);
  clearCartError();
  try {
    const resp = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items })
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.description || err.message || 'Could not add to cart.');
    }

    cartCount++;
    showToast(`&#10003; ${formulaName} for ${patientName} added to cart.`);
    resetTab(tab);
    chipExpanded[tab] = false;
    updatePanel(); updateFloatBar();

    fetch('/?sections=cart-icon-bubble')
      .then(r => r.json())
      .then(sections => {
        const el = document.getElementById('shopify-section-cart-icon-bubble');
        if (el && sections['cart-icon-bubble']) el.innerHTML = sections['cart-icon-bubble'];
      }).catch(() => {});

  } catch (err) {
    showCartError(err.message);
  } finally {
    setLoading(false);
  }
}

function resetTab(tab) {
  if (tab === 'herbal') {
    HS = { bottle: null, bottleMax: 0, material: 'glass', dropper: false, qtys: {}, essenceQtys: {}, loaded: null };
    document.getElementById('herb-formulaName').value = '';
    document.getElementById('herb-dosage').value = '';
    document.getElementById('herb-sp').value = '';
    document.getElementById('herb-loadedBanner').classList.remove('visible');
    setBottleUI(); renderHerbTable(); renderHerbEssences();
  } else if (tab === 'compound') {
    CS = { duration: 0, doses: {}, loaded: null };
    document.getElementById('comp-formulaName').value = '';
    document.getElementById('comp-dosage').value = '';
    document.getElementById('comp-sp').value = '';
    const durInp = document.getElementById('comp-durInp'); durInp.value = ''; durInp.classList.add('empty');
    document.getElementById('comp-loadedBanner').classList.remove('visible');
    onDurChange(); renderCompTable();
  } else {
    KS = { base: null, qtys: {}, essenceQtys: {}, loaded: null };
    document.getElementById('cream-formulaName').value = '';
    document.getElementById('cream-dosage').value = '';
    document.getElementById('cream-sp').value = '';
    document.getElementById('cream-loadedBanner').classList.remove('visible');
    renderCreamBases(); renderCreamTable(); renderCreamEssences();
  }
  const pn = document.getElementById('panelNotes'); if (pn) pn.value = '';
}

function confirmClear() { openModal('clearModal'); }
function doClear() {
  closeModal('clearModal');
  resetTab(activeTab);
  chipExpanded[activeTab] = false;
  updatePanel(); updateFloatBar();
  showToast('Formula cleared.');
}

/* ── CART ERROR ── */
function showCartError(msg) {
  const el = document.getElementById('cartError'); if (!el) return;
  el.textContent = msg; el.style.display = 'block';
  el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}
function clearCartError() {
  const el = document.getElementById('cartError'); if (el) { el.textContent = ''; el.style.display = 'none'; }
}

/* ── DRAWERS ── */
function openHerbDrawer(id, mode) {
  const h = HERBS.find(x => x.id === id); if (!h) return;
  drawerItem = h; drawerTab = mode || 'herbal';
  document.getElementById('dName').textContent = h.name;
  document.getElementById('dSub').textContent = h.vendor;
  const dImg = document.getElementById('dImg');
  if (h.image) { dImg.innerHTML = `<img src="${esc(h.image)}" alt=""/>`; } else { dImg.innerHTML = '&#127807;'; }
  const pills = [h.price !== null ? `$${h.price.toFixed(2)}/mL` : null, h.dosage].filter(Boolean);
  if (h.stock <= HERB_LOW_STOCK) pills.push(`LOW STOCK: ${h.stock}mL`);
  if (isNew(h.created)) pills.push('&#127807; New');
  document.getElementById('dPills').innerHTML = pills.map(p => `<span class="d-pill">${p}</span>`).join('');
  document.getElementById('dDesc').innerHTML = h.description || '';
  document.getElementById('dUnit').textContent = 'mL';
  const q = (mode === 'cream' ? KS.qtys : HS.qtys)[id] || 0;
  document.getElementById('dQty').value = q;
  document.getElementById('dMinus').disabled = q === 0;
  document.getElementById('dFoot').style.display = 'flex';
  openDrawerEl();
}
function openCompDrawer(id) {
  const ing = COMPOUNDS.find(x => x.id === id); if (!ing) return;
  drawerItem = ing; drawerTab = 'compound';
  document.getElementById('dName').textContent = ing.name;
  document.getElementById('dSub').textContent = ing.vendor;
  const dImg = document.getElementById('dImg');
  if (ing.image) { dImg.innerHTML = `<img src="${esc(ing.image)}" alt=""/>`; } else { dImg.innerHTML = '&#128300;'; }
  const pills = [ing.price !== null ? `$${ing.price.toFixed(2)}/g` : null, `Rec: ${ing.recDose}`].filter(Boolean);
  if (ing.stock <= COMP_LOW_STOCK) pills.push(`LOW STOCK: ${ing.stock}g`);
  if (isNew(ing.created)) pills.push('&#127807; New');
  document.getElementById('dPills').innerHTML = pills.map(p => `<span class="d-pill">${p}</span>`).join('');
  let desc = ing.description || '';
  if (ing.elemental) desc += (desc ? '\n\n' : '') + 'Ingredient breakdown: ' + ing.elemental;
  if (ing.warnings) desc += (desc ? '\n\n' : '') + '⚠ ' + ing.warnings;
  document.getElementById('dDesc').textContent = desc;
  document.getElementById('dUnit').textContent = 'g/day';
  const d = CS.doses[id] || 0;
  document.getElementById('dQty').value = d;
  document.getElementById('dMinus').disabled = d === 0;
  document.getElementById('dFoot').style.display = getDur() ? 'flex' : 'none';
  openDrawerEl();
}
function openEssDrawer(id, pfx) {
  const e = ESSENCES.find(x => x.id === parseInt(id)); if (!e) return;
  drawerItem = e; drawerTab = pfx;
  document.getElementById('dName').textContent = e.name;
  document.getElementById('dSub').textContent = 'Australian Bush Flower Essences';
  const dImg = document.getElementById('dImg');
  if (e.image) { dImg.innerHTML = `<img src="${esc(e.image)}" alt=""/>`; } else { dImg.innerHTML = '&#127804;'; }
  document.getElementById('dPills').innerHTML = `<span class="d-pill">${e.price !== null ? '$' + e.price.toFixed(2) + '/dose' : '—'}</span><span class="d-pill">7 drops per dose</span>`;
  document.getElementById('dDesc').innerHTML = e.description || '';
  document.getElementById('dFoot').style.display = 'none';
  openDrawerEl();
}
function openDrawerEl() { document.getElementById('overlay').classList.add('open'); document.getElementById('drawer').classList.add('open'); }
function closeDrawer() { document.getElementById('overlay').classList.remove('open'); document.getElementById('drawer').classList.remove('open'); drawerItem = null; drawerTab = null; }

function adjDrawer(delta) {
  if (!drawerItem) return;
  const cur = parseFloat(document.getElementById('dQty').value) || 0;
  const nv = Math.max(0, rnd(cur + delta, 3));
  document.getElementById('dQty').value = nv;
  syncDrawer();
}
function syncDrawer() {
  if (!drawerItem) return;
  const val = Math.max(0, parseFloat(document.getElementById('dQty').value) || 0);
  if (drawerTab === 'herbal') setHerbQty(drawerItem.id, Math.round(val));
  else if (drawerTab === 'compound') setCompDose(drawerItem.id, val);
  else if (drawerTab === 'cream') setCreamQty(drawerItem.id, Math.round(val));
  document.getElementById('dMinus').disabled = val === 0;
}

/* ── INIT ── */
document.addEventListener('DOMContentLoaded', () => {
  initBuilder();
  updatePanel();
  updateFloatBar();
  document.getElementById('overlay').addEventListener('click', closeDrawer);
  document.getElementById('drawer').addEventListener('click', e => e.stopPropagation());
  document.addEventListener('click', e => {
    if (!e.target.closest('.patient-field-wrap')) document.getElementById('patientDrop').classList.remove('open');
  });
});
